import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Tag, Button } from 'antd';
import { EyeOutlined, BulbOutlined } from '@ant-design/icons';
import { t } from '../../i18n';

interface Suggestion {
  id: number;
  subject: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  createdAt: string;
  user: {
    fullName: string;
    phone: string;
  };
  messages?: { content: string }[];
}

// "Taklif: Ilova" -> "Ilova"; otherwise return as-is
const parseCategory = (subject: string | undefined) => {
  if (!subject) return '-';
  const prefix = 'Taklif:';
  if (subject.startsWith(prefix)) return subject.slice(prefix.length).trim() || '-';
  return subject;
};

export const SuggestionList = () => {
  const { show } = useNavigation();

  const { tableProps } = useTable<Suggestion>({
    resource: 'admin/tickets',
    syncWithLocation: true,
    filters: {
      permanent: [
        { field: 'isEmergency', operator: 'eq', value: false },
        { field: 'isSuggestion', operator: 'eq', value: true },
      ],
    },
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
  });

  const statusLabels: Record<string, string> = {
    OPEN: t('suggestions.statusNew'),
    IN_PROGRESS: t('suggestions.statusUnderReview'),
    RESOLVED: t('suggestions.statusAccepted'),
    CLOSED: t('suggestions.statusClosed'),
  };

  const statusColors: Record<string, string> = {
    OPEN: 'blue',
    IN_PROGRESS: 'orange',
    RESOLVED: 'green',
    CLOSED: 'default',
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => <span style={{ fontWeight: 500 }}>#{id}</span>,
    },
    {
      title: t('suggestions.category'),
      dataIndex: 'subject',
      key: 'category',
      width: 160,
      render: (subject: string) => (
        <Tag color="gold">{parseCategory(subject)}</Tag>
      ),
    },
    {
      title: t('suggestions.preview'),
      key: 'preview',
      ellipsis: true,
      render: (_: unknown, record: Suggestion) => {
        const text = record.messages?.[0]?.content || '';
        return (
          <span style={{ color: '#2C3E50' }}>
            {text.length > 80 ? `${text.slice(0, 80)}...` : text || '-'}
          </span>
        );
      },
    },
    {
      title: t('support.ticketUser'),
      dataIndex: 'user',
      key: 'user',
      render: (user: Suggestion['user']) => (
        <div>
          <div>{user?.fullName || t('common.noName')}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{user?.phone}</div>
        </div>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: t('common.date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) =>
        new Date(date).toLocaleString('uz-UZ', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (_: unknown, record: Suggestion) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('suggestions', record.id)}
        />
      ),
    },
  ];

  return (
    <List
      title={
        <span>
          <BulbOutlined style={{ color: '#F39C12', marginRight: 8 }} />
          {t('suggestions.title')}
        </span>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
